/* ──────────────────────────────────────────────────────────────
   Stage 2 (L2 · remote unlock) — frontend journey rules.
   Mirrors CRM_Interactive_Journey_V15.html: which question shows
   next, the inline hints, and the Submit / Call Dropped popups.
   Questions + option ids still come from get-checklist; they are
   matched here by question text and option slug/label.
   ────────────────────────────────────────────────────────────── */

export const DEST = {
  CLOSE:    { label: 'Close ticket',              action: 'Close Ticket',             btn: 'ru-btn-success' },
  OPERATOR: { label: 'Operator',                  action: 'Assign to Operator',       btn: 'ru-btn-red' },
  CNC:      { label: 'Stage 3 · CNC',             action: 'Assign to CNC',            btn: 'ru-btn-primary' },
  CALLBACK: { label: 'Stage II Queue (callback)', action: 'Assign to Callback Queue', btn: 'ru-btn-plum' },
}

const norm = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

const MATCHERS = {
  near:     'rider near qis',
  dock:     'dock in dock out',
  verify:   'rider verification',
  swap:     'swap successful',
  handover: 'battery handover',
  reason:   'reason swap did not succeed',
  sendTo:   'send this case to',
}

/** get-checklist questions → { near, dock, verify, swap, handover, reason, sendTo } (null if not an L2 remote-unlock checklist) */
export function identifyL2(questions) {
  const map = {}
  Object.entries(MATCHERS).forEach(([k, m]) => {
    map[k] = questions.find(q => norm(q.text).includes(m)) || null
  })
  return map.near && map.swap ? map : null
}

const optOf = (q, answers) => (q ? q.options.find(o => o.id === String(answers[q.id] ?? '')) : null)

/** 'yes' | 'no' | '' for a Yes/No question */
export function yn(q, answers) {
  const o = optOf(q, answers)
  if (!o) return ''
  const v = norm(o.slug || o.label)
  return v === 'yes' ? 'yes' : v === 'no' ? 'no' : ''
}

/** 'CNC' | 'OPERATOR' for a reason / send-to option (from its slug or label ending) */
export function groupOf(opt) {
  if (!opt) return null
  const v = norm(opt.slug || opt.label)
  if (/operator$/.test(v)) return 'OPERATOR'
  if (/cnc$/.test(v)) return 'CNC'
  return null
}

/** send-to option id for a destination */
export function sendToOptionFor(map, dest) {
  return map.sendTo?.options.find(o => groupOf(o) === dest)?.id || ''
}

/** questions visible for the current answers (Q2 only after Q1 = Yes, …) */
export function l2Visible(map, answers) {
  const out = [map.near]
  if (yn(map.near, answers) !== 'yes') return out
  if (map.dock) { out.push(map.dock); if (yn(map.dock, answers) !== 'yes') return out }
  if (map.verify) { out.push(map.verify); if (yn(map.verify, answers) !== 'yes') return out }
  out.push(map.swap)
  const swap = yn(map.swap, answers)
  if (swap === 'yes' && map.handover) out.push(map.handover)
  if (swap === 'no' && map.reason) {
    out.push(map.reason)
    if (answers[map.reason.id] && map.sendTo) out.push(map.sendTo)
  }
  return out
}

/** suggested destination from the selected reason */
export function suggestedDest(map, answers) {
  return groupOf(optOf(map.reason, answers))
}

/** inline hints shown under questions → { [questionId]: { text, tone: 'bad'|'ok'|'info'|'warn' } } */
export function l2Hints(map, answers) {
  const h = {}
  if (yn(map.near, answers) === 'no') h[map.near.id] = { text: '→ Rider not at the QIS — the ticket will be closed', tone: 'bad' }
  if (map.dock && yn(map.dock, answers) === 'no') h[map.dock.id] = { text: '→ Dock details not verified — route to Operator or CNC', tone: 'bad' }
  if (map.verify && yn(map.verify, answers) === 'no') h[map.verify.id] = { text: '→ Rider verification failed — route to Operator or CNC', tone: 'bad' }
  if (map.handover) {
    const ho = yn(map.handover, answers)
    if (ho === 'yes') h[map.handover.id] = { text: '→ Ticket closes here', tone: 'ok' }
    if (ho === 'no') h[map.handover.id] = { text: '→ Assign to CNC for CCTV review', tone: 'info' }
  }
  const sug = suggestedDest(map, answers)
  if (map.reason && sug) h[map.reason.id] = { text: `Suggested: ${DEST[sug].label}`, tone: sug === 'OPERATOR' ? 'bad' : 'info' }
  if (map.sendTo && answers[map.sendTo.id]) {
    const chosen = groupOf(optOf(map.sendTo, answers))
    h[map.sendTo.id] = sug && chosen && chosen !== sug
      ? { text: `⚠ Overriding the suggested route (${DEST[sug].label}). The override is logged.`, tone: 'warn' }
      : { text: 'Matches the suggested route for this reason.', tone: 'muted' }
  }
  return h
}

/** ✓ / ✗ chips for the popups */
export function l2Pills(map, answers) {
  return [
    ['near', 'Rider near QIS'], ['dock', 'Dock verified'], ['verify', 'Rider verification'],
    ['swap', 'Swap successful'], ['handover', 'Battery handover'],
  ]
    .map(([k, label]) => ({ k, label, v: yn(map[k], answers) }))
    .filter(p => p.v)
    .map(p => ({ key: p.k, label: p.label, ok: p.v === 'yes' }))
}

const reasonText = (opt) => (opt ? opt.label.split('→')[0].trim() : '')

/**
 * Submit popup. Returns { tone, title, subtitle, body, notes[], actions:[dest], note }
 * body is an array of { label?, text } lines.
 */
export function l2SubmitOutcome(map, answers) {
  const near = yn(map.near, answers)
  if (near === 'no') return {
    tone: 'warning', title: 'Rider is not at the QIS',
    subtitle: 'A remote unlock cannot proceed without the rider present.',
    body: [{ text: 'The rider is not near the station, so the session cannot run. Close the ticket and ask the rider to call back when they are at the QIS.' }],
    actions: ['CLOSE'], note: 'Rider not at the QIS',
  }
  if (map.dock && yn(map.dock, answers) === 'no') return {
    tone: 'danger', title: 'Dock details not verified',
    subtitle: 'Dock-in / dock-out could not be confirmed with the rider.',
    body: [{ text: 'Without verified dock numbers the unlock cannot be targeted safely. Route the case for physical or CNC follow-up.' }],
    actions: ['OPERATOR', 'CNC'], note: 'Dock-in / dock-out not verified with rider',
  }
  if (map.verify && yn(map.verify, answers) === 'no') return {
    tone: 'danger', title: 'Rider verification failed',
    subtitle: 'Keyfob verification did not succeed.',
    body: [{ text: 'The rider could not be verified at the station, so the unlock cannot be authorised from Stage 2.' }],
    actions: ['OPERATOR', 'CNC'], note: 'Rider verification failed',
  }
  if (yn(map.swap, answers) === 'no') {
    const reason = optOf(map.reason, answers)
    const dest = groupOf(optOf(map.sendTo, answers)) || suggestedDest(map, answers) || 'CNC'
    const sug = suggestedDest(map, answers)
    return {
      tone: dest === 'OPERATOR' ? 'danger' : 'info', title: 'Swap did not succeed',
      subtitle: 'Destination taken from the agent selection.',
      body: [
        { label: 'Reason:', text: reasonText(reason) || '—' },
        { label: 'Routing to:', text: DEST[dest].label },
        ...(sug && sug !== dest ? [{ warn: true, text: `⚠ Agent overrode the suggested route (${DEST[sug].label}).` }] : []),
      ],
      actions: [dest], note: reasonText(reason) || 'Swap failed',
    }
  }
  if (map.handover && yn(map.handover, answers) === 'no') return {
    tone: 'info', title: 'Handover not confirmed',
    subtitle: 'Swap succeeded but the rider did not confirm receiving the battery.',
    body: [{ text: 'The dock released the battery but handover is unconfirmed. CNC reviews footage for this station and window.' }],
    actions: ['CNC'], note: 'Handover not confirmed at Stage 2',
  }
  return {
    tone: 'success', title: 'Swap and handover confirmed',
    subtitle: 'Both checks passed — the case closes here.',
    body: [{ text: 'Battery handover confirmed with the rider. Close the call and the ticket.' }],
    notes: [{ label: 'A3 (Blocking):', text: 'handover rests on the rider’s word — CNC cannot yet confirm the BP entered the designated vehicle.' }],
    actions: ['CLOSE'], note: 'Swap and handover confirmed',
  }
}

/** Call Dropped popup → { subtitle, body, dest, note } (first matching rule wins) */
export function l2DropOutcome(map, answers) {
  const near = yn(map.near, answers)
  const swap = yn(map.swap, answers)
  const ho = yn(map.handover, answers)
  if (!near) return {
    dest: 'CLOSE', subtitle: 'Stage II — dropped before any check answered',
    body: 'The call dropped before the first check was answered, so there is nothing to re-attempt on a callback. The ticket is closed.',
  }
  if (near === 'no') return {
    dest: 'CLOSE', subtitle: 'Stage 2 — rider not at the QIS',
    body: 'The rider was not near the station, so there is nothing to re-attempt on a callback. The ticket is closed.',
  }
  if (ho === 'no') return {
    dest: 'CNC', subtitle: 'Stage II — handover not confirmed',
    body: 'Battery handover was marked No, so the case must be validated by CNC rather than re-attempted on a callback.',
  }
  if (swap === 'yes' && !ho) return {
    dest: 'CNC', subtitle: 'Stage 2 — dropped after swap, before handover confirmed',
    body: 'The swap succeeded but the call dropped before handover could be confirmed. The battery may already be out of the dock, so CNC must validate the case rather than a callback.',
  }
  return {
    dest: 'CALLBACK', subtitle: 'Stage 2 — call dropped mid-session',
    body: `The call dropped mid-session. The case goes to the ${DEST.CALLBACK.label} for a re-attempt and the QIS session lock is released.`,
  }
}

export const A12_NOTE = {
  label: 'A12 required:',
  text: 'drops are agent-marked. Without an Ozonetel CTI event into iCARE, a closed browser leaves the ticket and the QIS lock stranded.',
}

/* generic names used by StageForm (same shape as stage3Rules) */
export const identify = identifyL2
export const visible = l2Visible
export const hints = l2Hints
export const pills = l2Pills
export const submitOutcome = l2SubmitOutcome
export const dropOutcome = l2DropOutcome
export const notes = () => ({})
export const contextLine = () => null
export const dropButtonLabel = '📞 Call Dropped'
export const summaryTitle = 'Stage 2 Summary'
